import { useCallback } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { useNavigation } from 'expo-router';

import { IRestaurant } from '@rbi-ctg/store';
import { useDismissAllAndNavigate } from 'navigation/hooks/use-dismiss-and-navigate';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { routes } from 'utils/routing';

import { useDefaultServiceMode } from './use-default-service-mode';

interface IUseSelectRestaurantOptions {
  restaurant: IRestaurant;
}

interface ISelectRestaurantOptions {
  callback?: () => void;
  requestedServiceMode?: ServiceMode;
}
export function useSelectStore({ restaurant }: IUseSelectRestaurantOptions) {
  const {
    selectStore,
    reorder: { pendingReorder, setShouldHandleReorder },
  } = useOrderContext();
  const { setServiceMode } = useServiceModeContext();
  const router = useRouter();

  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const defaultServiceMode = useDefaultServiceMode({ restaurant });
  const dismissAndNavigate = useDismissAllAndNavigate();
  const { redirectTo } = useLocalSearchParams<{ redirectTo: keyof typeof routes }>();
  const navState = useNavigation().getState();
  const isStoreInfoModal = navState.index > 0;
  const { prevPathname } = useUIContext();

  const selectStoreCallback = useCallback(
    (requestedServiceMode = defaultServiceMode) => async () => {
      if (!enableOrdering) {
        dismissAndNavigate(routes.offers);
        return;
      }

      logEvent(CustomEventNames.ORDER_HERE_STORE_CONFIRMATION_FLOW, EventTypes.Navigation, {
        'Restaurant Id': restaurant._id,
        'Restaurant Name': restaurant.name,
      });
      // set service mode to ensure we aren't taken back to static menu
      // need to do this after previous checks so that we don't set service mode
      // when enableOrdering is false
      setServiceMode(requestedServiceMode);

      // if user has a pending reorder return and let the useEffect handle it below
      if (pendingReorder) {
        setShouldHandleReorder(true);
        return;
      }

      // Handle navigation based on context
      if (redirectTo) {
        // If there's a specific redirect target, prioritize it
        router.navigate(redirectTo);
      } else if (prevPathname === routes.base) {
        // If coming from home go to menu directly
        router.navigate(routes.menu);
      } else if (isStoreInfoModal) {
        // If we're in a store info modal, dismiss and go back
        router.dismiss();
        router.back();
      } else if (router.canDismiss()) {
        // If we can dismiss the current screen, do so
        router.dismiss();
      } else {
        // Fallback: navigate to menu if no other conditions match
        router.navigate(routes.menu);
      }

      LocalStorage.setItem(StorageKeys.SKIP_AUTO_SELECT_PROMPT, true);
    },
    [
      defaultServiceMode,
      enableOrdering,
      pendingReorder,
      restaurant._id,
      restaurant.name,
      setServiceMode,
      redirectTo,
      prevPathname,
      isStoreInfoModal,
      router,
      dismissAndNavigate,
      setShouldHandleReorder,
    ]
  );

  const selectRestaurant = useCallback(
    async ({ requestedServiceMode = defaultServiceMode }: ISelectRestaurantOptions = {}) => {
      // FIXME: IRestaurantNode can't be safely cast to IStore
      const callback = selectStoreCallback(requestedServiceMode);
      await selectStore(restaurant, callback, requestedServiceMode, true);
    },
    [defaultServiceMode, selectStore, restaurant, selectStoreCallback]
  );

  return {
    selectRestaurant,
  };
}
